import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { CtaBand } from "../components/site/CtaBand";
import { Reveal, EASE } from "../lib/motion";
import { useSEO, useLenis } from "../lib/hooks";
import { BLOG_POSTS } from "../data/blogPosts";

const Blogs = () => {
  useLenis();
  useSEO({
    title: "JSC Blog — Fashion Retail Insights and Data Analytics for Indian Retailers",
    description:
      "Fashion retail insights India: JSC's retail data analytics blog turns industry reports into plain, practical business intelligence for fashion brands, powered by ForesightFlow.",
    keywords:
      "fashion retail analytics India, retail business intelligence blog, Indian fashion retail trends, inventory intelligence software, retail data insights, ForesightFlow, Jay Shah Consultancy, small business analytics India",
  });

  return (
    <div className="jsc-grain relative bg-[#FAF8F5] min-h-screen" data-testid="page-blogs">
      <Navbar />

      {/* 1. HERO */}
      <section className="relative pt-36 md:pt-44 lg:pt-48 pb-16 md:pb-20 overflow-hidden">
        <div className="jsc-gridlines absolute inset-0 opacity-30 [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <p className="font-body text-xs md:text-sm uppercase tracking-[0.24em] text-[#1C1C1E] mb-8 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[#B39B77]" />
              The JSC Blog
            </p>
          </Reveal>
          <h1
            data-testid="blogs-headline"
            className="font-display font-semibold text-[#1C1C1E] text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-5xl"
          >
            What&apos;s happening in Indian fashion retail — and what it means for your business.
          </h1>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl font-body text-lg md:text-xl leading-relaxed text-[#3E4A5C]">
              We track the industry reports and news so you don&apos;t have to — then add our own
              read on what each finding actually means for day-to-day decisions on your shop floor.
              Fashion retail insights, in plain language, from the JSC analytics team.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 2. BLOG GRID */}
      <section className="relative pb-24 md:pb-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {BLOG_POSTS.map((post, i) => (
              <motion.article
                key={post.slug}
                data-testid={`blog-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease: EASE, delay: (i % 3) * 0.08 }}
                className="group flex flex-col bg-[#FAF8F5] border border-[rgba(28,28,30,0.12)] rounded-[3px] p-8 transition-colors duration-500 hover:border-[#B39B77]"
              >
                <span className="font-body text-[10px] uppercase tracking-[0.2em] text-[#B39B77]">
                  {post.feature}
                </span>
                <h2 className="mt-4 font-display text-2xl leading-snug text-[#1C1C1E]">
                  <Link to={`/blogs/${post.slug}`} data-testid={`blog-card-link-${i}`} className="hover:text-[#3E4A5C] transition-colors duration-300">
                    {post.title}
                  </Link>
                </h2>
                <p className="mt-4 font-body text-base leading-relaxed text-[#3E4A5C] flex-1">
                  {post.teaser}
                </p>
                <p className="mt-6 font-body text-xs text-[#3E4A5C]/70">Source: {post.source}</p>
                <Link
                  to={`/blogs/${post.slug}`}
                  className="mt-5 inline-flex items-center gap-1.5 font-body text-sm font-medium text-[#1C1C1E] link-underline w-fit"
                >
                  Read the full breakdown
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* 3. CLOSING CTA */}
      <CtaBand
        eyebrow="Make it personal"
        title="Want insights like this built around your own store's data?"
        testid="blogs-cta"
      />

      <Footer />
    </div>
  );
};

export default Blogs;
